import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { useTranslation } from "react-i18next";
import { HexagonalRequirements } from "../models/HexagonalRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
    UnitInput,
} from "./PatternFormControls";

// Add interface for component props
interface HexagonalPatternFormProps {
  seamlessResult: any;
  setSeamlessResult: any;
  uploadedImages?: any[];
}

export const HexagonalPatternForm = ({seamlessResult, setSeamlessResult, uploadedImages = []}: HexagonalPatternFormProps) => {
    const { t } = useTranslation();
    const { language } = useSelector((state: VendorState) => state.settings.theme);
    const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false)
    const textureAPI = useTextureApi()
    const dispatch = useDispatch()
    const [fields, setFields] = useState({
      L1: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.HEXAGONAL_HEIGHT')}`,
            name: "L1",
            value: "",
      unit: "cm"
        },
        L2: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.HEXAGONAL_WIDTH')}`,
            name: "L2",
            value: "",
      unit: "cm"
        },
        textureWidth: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
            name: "textureWidth",
      value: "2",
      unit: "m"
        },
        textureHeight: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.TEXTURE_HEIGHT')}`,
            name: "textureHeight",
      value: "2",
      unit: "m"
        },
        groutLine: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
            name: "groutLine",
      value: "2",
      unit: "mm"
        },
        groutColor: {
            isTouched: false,
            label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
            name: "groutColor",
      value: "#CCCCCC"
        }
  });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFields({...fields, [name]: {
            ...fields[name],
            value: value,
            isTouched: true
    }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

    const isValidStep = () => {
    return fields.L1.value && fields.L2.value && fields.textureWidth.value && fields.textureHeight.value && uploadedImages.length > 0;
  };

    const handleGroutColorChange = (color) => {
      setFields({
        ...fields,
        groutColor: {
          ...fields.groutColor,
          value: color.hex + Math.round(color.rgb.a * 255).toString(16)
        }
      });
    };

    const handleGenerateSeamless = async () => {
      if(isValidStep()){
        try {
          dispatch(setLoading(true))
          const body = new HexagonalRequirements()
          body.setFiles(uploadedImages)
              .setLengthL1(fields.L1.value)
              .setLengthL2(fields.L2.value)
              .setTextureWidth(fields.textureWidth.value)
              .setTextureHeight(fields.textureHeight.value)
              .setGroutLineColor(fields.groutColor.value || "#CCCCCC")
              .setGroutLineWidth(fields.groutLine.value)
          let data = await textureAPI.generateTextureForPattern(body, "hexagonal");
          setSeamlessResult(data)
        } catch (error) {
          console.error('Error generating seamless texture:', error);
          setSeamlessResult(null);
        } finally {
          dispatch(setLoading(false))
        }
      }
  };

    return (
    <div>
      {/* Hidden button for the main component to trigger */}
      <button
        type="button"
        id="hexagonal-generate-btn"
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.DIMENSIONS')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.L1.label}</PatternFieldLabel>
            <UnitInput
              value={fields.L1.value}
              name="L1"
              onChange={handleChange}
              type="number"
              unit={fields.L1.unit}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.L2.label}</PatternFieldLabel>
            <UnitInput
              value={fields.L2.value}
              name="L2"
              onChange={handleChange}
              type="number"
              unit={fields.L2.unit}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureWidth"
              value={fields.textureWidth.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.textureHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureHeight"
              value={fields.textureHeight.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>
    </div>
    );
};
